import * as Y from 'yjs';
import { Observable, Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { SlideType } from '@pcp/core/interfaces';
import {
    DEFAULT_DEACTIVATED_HOTSPOTS,
    DEFAULT_PERCEPTION_REVEAL_STATE,
    DisplayStateSnapshot,
    DRAWING_METADATA_MAP_KEYS,
    DRAWING_OBJECT_ARRAY_KEYS,
    DYNAMIC_CONTENT_ARRAY_KEYS,
    HTML_ELEMENT_MUTATION_ARRAY_KEYS,
    LESSON_MAP_KEY,
    LESSON_SCOPED_KEYS,
    LessonStatus,
    parseDeactivatedHotspots,
    parsePerceptionRevealState,
    PerceptionRevealState,
    PRIMARY_SLIDE_HISTORY_KEY,
    PrimarySlideHistoryEntry,
    SYNCED_LESSON_STATE_DEFAULTS,
    SYNCED_LESSON_STATE_KEYS,
    SyncedLessonState,
} from '../../public/yjs/lesson-ydoc-schema';
import { emitAfterTransaction } from '../boundary/emit-after-transaction';

/**
 * Minimal error-logger contract used by `LessonYjsBinding` so it can stay
 * pure TypeScript / Angular-free. Callers may pass a scoped PcpLogger directly.
 */
export interface LessonYjsBindingErrorLogger {
    error(message: string, error?: Error, extras?: Record<string, unknown>): void;
}

const NOOP_LOGGER: LessonYjsBindingErrorLogger = { error: () => undefined };

/**
 * LessonYjsBinding — Yjs-primary binding between a Y.Doc and lesson state.
 *
 * WRITE direction (Effects → Y.Doc):
 *   Granular write methods (`pauseLesson()`, `resumeLesson()`, etc.) modify the Y.Map.
 *   The observer emits these changes just like remote changes — one flow for everyone.
 *
 * READ direction (Y.Doc → NgRx):
 *   The `changes$` observable emits partial state updates for ALL Y.Map changes
 *   (both local and remote). No origin filtering — Yjs is the source of truth.
 *
 * Pure TypeScript — no Angular dependencies.
 */
export class LessonYjsBinding {
    /** Transaction origin used to suppress local observer for writes that don't need NgRx dispatch. */
    private static readonly LOCAL_ONLY = 'local-only';

    /** Fresh (hidden, epoch 0) perception reveal-state, written on every slide show / layer clear. */
    private static readonly FRESH_REVEAL_STATE = JSON.stringify(DEFAULT_PERCEPTION_REVEAL_STATE);

    /** Fresh (empty) hotspot-deactivation state, written on every slide show / layer clear / reset. */
    private static readonly FRESH_DEACTIVATED_HOTSPOTS = JSON.stringify(DEFAULT_DEACTIVATED_HOTSPOTS);

    /**
     * Mint an identity for a new *showing* of a slide (`slide-showing-identity`).
     *
     * Called from every showing transition — `setSlide` (target + cascaded layers),
     * `resetSlideToInitial`, `removeSlide` — and from nowhere else: writes that change
     * state *within* a showing (viewBox, textboard page, scroll-video frame, reveal,
     * hotspot deactivation) deliberately keep the current id.
     *
     * A UUID rather than a monotonic counter, unlike `PerceptionRevealState.epoch`: the
     * case this defends against is two concurrent writers, who would derive the same next
     * counter value from the same observed state and mint colliding "different" showings.
     * See {@link SyncedLessonState.primarySlideShowingId}.
     *
     * `uuid`'s `v4()` rather than `crypto.randomUUID()`: the latter is secure-context only,
     * so it is `undefined` on a beamer opened over plain HTTP from another machine on the
     * classroom LAN. `v4()` falls back to `crypto.getRandomValues`, which is always exposed.
     */
    private static mintShowingId(): string {
        return uuidv4();
    }

    private readonly lessonMap: Y.Map<unknown>;
    private readonly mutationArrays: Record<1 | 2 | 3, Y.Array<string>>;
    private readonly dynamicContentArrays: Record<1 | 2 | 3, Y.Array<string>>;
    /**
     * Per-layer vector drawing objects (fabric.js canvas migration). Unlike the
     * JSON-string mutation/dynamic-content arrays, these hold `Y.Map` objects so the
     * `FabricYjsBinding` (owned by the data-access-internal drawing-canvas service)
     * can bind to and reconcile the canvas directly. The binding here only creates,
     * exposes (internally), and cascade-clears these arrays on slide change; it does
     * NOT observe them — the canvas reconciliation is the `FabricYjsBinding`'s job.
     */
    private readonly drawingObjectArrays: Record<1 | 2 | 3, Y.Array<Y.Map<unknown>>>;
    private readonly primarySlideHistory: Y.Array<PrimarySlideHistoryEntry>;
    private readonly changesSubject = new Subject<Partial<SyncedLessonState>>();
    private readonly mutationBatchSubject = new Subject<{ layer: 1 | 2 | 3; batch: string }>();
    private readonly mutationBatchesClearedSubject = new Subject<{ layer: 1 | 2 | 3 }>();
    private readonly dynamicContentAddedSubject = new Subject<{ layer: 1 | 2 | 3; itemJson: string }>();
    private readonly dynamicContentClearedSubject = new Subject<{ layer: 1 | 2 | 3 }>();
    private readonly historySubject = new Subject<PrimarySlideHistoryEntry[]>();
    private observer?: (event: Y.YMapEvent<unknown>, transaction: Y.Transaction) => void;
    private mutationObservers: Array<() => void> = [];
    private dynamicContentObservers: Array<() => void> = [];
    private historyObservers: Array<() => void> = [];

    readonly changes$: Observable<Partial<SyncedLessonState>> = this.changesSubject.asObservable();
    /**
     * Emits when a remote DomMutation[] batch is added to any layer's Y.Array.
     * Local INSERTS are intentionally suppressed (`transaction.local` filter in
     * the `delta.insert` branch of `attachMutationObservers`) — the writer has
     * already applied the mutations to its own DOM, so re-dispatching would
     * cause double-application.
     */
    readonly mutationBatchAdded$: Observable<{ layer: 1 | 2 | 3; batch: string }> = this.mutationBatchSubject.asObservable();
    /**
     * Emits when `clearMutations()` empties a layer's Y.Array (i.e. the array
     * transitions to length 0 after a delete event) — on BOTH writer and
     * receiver.
     *
     * Symmetric on writer and receiver (task 14, 2026-05-15): the slide-layer
     * UI reaction (svgCanvas teardown, mutation-command-stream replay) reacts
     * to this event identically on both sides. The writer's append-only
     * renderer doesn't visually clear `<path>` elements just because the Y.Array
     * was emptied — it needs the same teardown signal the receiver gets.
     *
     * The emission is deferred via `emitAfterTransaction` so it runs AFTER `Y.applyUpdate`
     * (or the local writer's `transact` callback) completes. This avoids
     * re-entrancy of downstream subscribers (cmdStream replay, createResetDynamicContent$
     * → svgCanvas teardown) running inside the Y.Doc transaction context. See task 14
     * Phase 1 findings for the failure mode that motivates the defer.
     *
     * Only fires on full-clears (the current `clearMutations` semantics) AND
     * when the same transaction does NOT change slide identity (a `setSlide` /
     * `removeSlide` cascade that also clears mutations should NOT trigger a
     * user-reset-style teardown on the freshly-rendered new slide). Partial
     * deletes would not emit; if introduced in the future, the observer's
     * `event.target.length === 0` guard needs revisiting.
     */
    readonly mutationBatchesCleared$: Observable<{ layer: 1 | 2 | 3 }> = this.mutationBatchesClearedSubject.asObservable();
    /**
     * Emits each item appended to a layer's dynamic-content Y.Array, on BOTH
     * writer and receiver.
     *
     * Deliberately symmetric — unlike `mutationBatchAdded$` which suppresses
     * local inserts. The writer of a dynamic-content item (text extension,
     * perception image, bullet point) has NO pre-write local DOM mutation
     * path; its UI updates from this same event, so suppressing locals would
     * mean the writer's own UI never reflects its own appends. (Drawings
     * differ: the writer paints the stroke locally during the drag gesture,
     * so re-emitting the Y.Array insert would double-apply.)
     *
     * `itemJson` is the raw JSON string as written to the Y.Array; the
     * `ClassroomFacade` parses it before exposing a typed Observable to
     * consumers.
     */
    readonly dynamicContentAdded$: Observable<{ layer: 1 | 2 | 3; itemJson: string }> = this.dynamicContentAddedSubject.asObservable();
    /**
     * Emits when `clearDynamicContent()` empties a layer's Y.Array, on BOTH
     * writer and receiver. Same semantics as `mutationBatchesCleared$`:
     *  - `emitAfterTransaction` defer to run after the Y.Doc transaction closes;
     *  - suppressed when the same transaction also writes a `<layer>Slide`
     *    identity field (i.e. on `setSlide` / `removeSlide` cascade clears
     *    — the freshly-rendered new slide shouldn't trigger a same-slide
     *    reset reaction). Fires on `resetSlideDynamicContent()` and
     *    `resetSlideToInitial()` because neither touches slide identity.
     */
    readonly dynamicContentCleared$: Observable<{ layer: 1 | 2 | 3 }> = this.dynamicContentClearedSubject.asObservable();
    /** Emits the full primary-slide history snapshot whenever the underlying Y.Array changes (local or remote). */
    readonly primarySlideHistory$: Observable<PrimarySlideHistoryEntry[]> = this.historySubject.asObservable();

    constructor(
        private readonly yDoc: Y.Doc,
        private readonly logger: LessonYjsBindingErrorLogger = NOOP_LOGGER,
    ) {
        this.lessonMap = yDoc.getMap(LESSON_MAP_KEY);
        this.mutationArrays = {
            1: yDoc.getArray<string>(HTML_ELEMENT_MUTATION_ARRAY_KEYS[1]),
            2: yDoc.getArray<string>(HTML_ELEMENT_MUTATION_ARRAY_KEYS[2]),
            3: yDoc.getArray<string>(HTML_ELEMENT_MUTATION_ARRAY_KEYS[3]),
        };
        this.dynamicContentArrays = {
            1: yDoc.getArray<string>(DYNAMIC_CONTENT_ARRAY_KEYS[1]),
            2: yDoc.getArray<string>(DYNAMIC_CONTENT_ARRAY_KEYS[2]),
            3: yDoc.getArray<string>(DYNAMIC_CONTENT_ARRAY_KEYS[3]),
        };
        this.drawingObjectArrays = {
            1: yDoc.getArray<Y.Map<unknown>>(DRAWING_OBJECT_ARRAY_KEYS[1]),
            2: yDoc.getArray<Y.Map<unknown>>(DRAWING_OBJECT_ARRAY_KEYS[2]),
            3: yDoc.getArray<Y.Map<unknown>>(DRAWING_OBJECT_ARRAY_KEYS[3]),
        };
        this.primarySlideHistory = yDoc.getArray<PrimarySlideHistoryEntry>(PRIMARY_SLIDE_HISTORY_KEY);
        this.attachObserver();
        this.attachMutationObservers();
        this.attachDynamicContentObservers();
        this.attachHistoryObserver();
    }

    // --- WRITE methods (granular) ---

    pauseLesson(): void {
        this.lessonMap.set('paused', true);
    }

    resumeLesson(): void {
        this.lessonMap.set('paused', false);
    }

    setLessonStatus(status: LessonStatus): void {
        this.lessonMap.set('lessonStatus', status);
    }

    setPauseImageUrl(url: string | null): void {
        this.lessonMap.set('pauseImageUrl', url);
    }

    showRegistrationCode(): void {
        this.lessonMap.set('isShowingStudentRegistrationCode', true);
    }

    hideRegistrationCode(): void {
        this.lessonMap.set('isShowingStudentRegistrationCode', false);
    }

    beginLessonStop(): void {
        this.lessonMap.set('isLessonStopPending', true);
    }

    endLessonStop(): void {
        this.lessonMap.set('isLessonStopPending', false);
    }

    openExtensionApp(extensionApp: string): void {
        this.yDoc.transact(() => {
            this.lessonMap.set('activeExtensionApp', extensionApp);
        });
    }

    closeExtensionApp(): void {
        this.yDoc.transact(() => {
            this.lessonMap.set('activeExtensionApp', null);
        });
    }

    private static readonly LAYER_PREFIX: Record<1 | 2 | 3, 'primary' | 'secondary' | 'tertiary'> = {
        1: 'primary',
        2: 'secondary',
        3: 'tertiary',
    };

    /**
     * Seed the primary-slide history from the CURRENT `primarySlide` Y.Map value —
     * called once on fresh-lesson start, after `importDisplayState()` has carried
     * the draft's display state into this document.
     *
     * History is lesson-scoped and never crosses a handoff (`exportDisplayState()`
     * does not copy it), so the slide the teacher is on at lesson start must be
     * re-derived from the carried primary slide rather than carried itself. Skips
     * the empty-sentinel slide (it only clears the beamer display and would fail
     * server-side history validation) and pushes only when the history Y.Array is
     * empty — on a reconnect the restored Redis state already contains the history
     * and must not be duplicated.
     *
     * This is what remains of the deleted `initializeSlideState()`: its slide-carry
     * half is subsumed by `importDisplayState()`; only the history-seed side effect
     * survives, now derived from the imported state instead of a caller payload.
     *
     * `lectionId` cannot be re-derived from the carried slide — a slide knows only its
     * `parentId`, whose meaning depends on nesting depth, and this lib deliberately has
     * no module-structure knowledge. The caller reads it off the *draft* document's last
     * history entry before the handoff destroys it (see `LessonYjsService.initialize`),
     * which describes the very slide being carried. `null` when the draft recorded no
     * history — the seed entry is then unattributed rather than wrong.
     */
    seedPrimarySlideHistoryFromCurrentSlide(lectionId: number | null): void {
        if (this.primarySlideHistory.length > 0) return;
        const json = this.lessonMap.get('primarySlide') as string | null | undefined;
        if (!json) return;
        let parsed: Partial<{ id: number; moduleId: number; versionId: number; type: SlideType }>;
        try {
            parsed = JSON.parse(json) as typeof parsed;
        } catch (err) {
            this.logger.error('seedPrimarySlideHistoryFromCurrentSlide: corrupted primarySlide JSON — seed skipped',
                err as Error, { jsonPrefix: json.slice(0, 120) });
            return;
        }
        if (parsed.type === SlideType.empty) return;
        if (typeof parsed.id !== 'number' || typeof parsed.moduleId !== 'number'
            || typeof parsed.versionId !== 'number' || parsed.type === undefined) {
            this.logger.error('seedPrimarySlideHistoryFromCurrentSlide: primarySlide JSON missing history fields — seed skipped',
                undefined, { jsonPrefix: json.slice(0, 120) });
            return;
        }
        this.primarySlideHistory.push([{
            moduleId: parsed.moduleId,
            versionId: parsed.versionId,
            lectionId,
            slideId: parsed.id,
            shownAt: new Date().toISOString(),
        }]);
    }

    /**
     * The per-showing reset core shared by every "fresh showing" transition — `setSlide`
     * (target layer + cascaded layers), `removeSlide` and `resetSlideToInitial`. MUST be
     * called inside the caller's `yDoc.transact`, so the new showing id is atomic with
     * the slide identity it belongs to.
     *
     * One helper instead of six hand-written blocks, because every per-showing field must
     * be reset at ALL of them: a site missing one would leave that layer carrying the
     * previous showing's value — for the showing id specifically, stale drawings would
     * then match the "new" showing and render, silently defeating `slide-showing-identity`.
     * Add the next per-showing field HERE, not at a call site.
     *
     * The trailing clears cascade mutations + dynamic content + drawings from the old
     * showing (a slide-identity write in the same transaction suppresses the `*Cleared$`
     * events — they only fire on user-driven resets).
     */
    private resetLayerForNewShowing(layer: 1 | 2 | 3, opts: {
        /** New slide payload, `'closed'` (layer removed / cascade-cleared), or `'unchanged'` (same-slide reset). */
        slide: { json: string; isAccessedViaHotspot: boolean } | 'closed' | 'unchanged';
        /** Reset viewBox/textboardPage to defaults (`removeSlide` historically leaves them; the layer is closed anyway). */
        resetViewport: boolean;
        /**
         * `'fresh'`: never-revealed default for a new/closed slide. `'bump-epoch'`: same
         * slide re-shown — bump the show epoch so remote clients re-run the hide animation
         * even when `revealed` was already false (a plain `revealed: false` write would be
         * memoized away); with the slide identity unchanged, the epoch is the only signal
         * that re-arms the beamer's animation.
         */
        revealState: 'fresh' | 'bump-epoch';
    }): void {
        const prefix = LessonYjsBinding.LAYER_PREFIX[layer];
        if (opts.slide === 'closed') {
            this.lessonMap.set(`${prefix}Slide`, null);
            this.lessonMap.set(`${prefix}SlideIsAccessedViaHotspot`, false);
        } else if (opts.slide !== 'unchanged') {
            this.lessonMap.set(`${prefix}Slide`, opts.slide.json);
            this.lessonMap.set(`${prefix}SlideIsAccessedViaHotspot`, opts.slide.isAccessedViaHotspot);
        }
        if (opts.resetViewport) {
            this.lessonMap.set(`${prefix}SlideViewBox`, '0 0 1280 720');
            this.lessonMap.set(`${prefix}SlideTextboardPage`, 1);
        }
        if (opts.revealState === 'fresh') {
            // A freshly shown slide is never revealed — re-hides any prior perception reveal.
            this.lessonMap.set(`${prefix}SlideRevealState`, LessonYjsBinding.FRESH_REVEAL_STATE);
        } else {
            const { epoch } = this.readPerceptionRevealState(prefix);
            this.lessonMap.set(
                `${prefix}SlideRevealState`,
                JSON.stringify({ epoch: epoch + 1, revealed: false } satisfies PerceptionRevealState),
            );
        }
        // A fresh showing re-arms every hotspot — clears any prior deactivation (D6/C14).
        // No epoch needed: clearing a non-empty set is itself the change.
        this.lessonMap.set(`${prefix}SlideDeactivatedHotspots`, LessonYjsBinding.FRESH_DEACTIVATED_HOTSPOTS);
        // A scroll-video returns to its seed frame (null → `current ?? start`).
        this.lessonMap.set(`${prefix}SlideScrollVideoFrame`, null);
        // A fresh showing gets a fresh identity, written in the SAME transaction so no observer
        // can see a new slide carrying the previous showing's id (`slide-showing-identity`).
        // Drawings are stamped with it; a stale-stamped drawing cannot render on this showing.
        this.lessonMap.set(`${prefix}SlideShowingId`, LessonYjsBinding.mintShowingId());
        this.clearMutations(layer);
        this.clearDynamicContent(layer);
        this.clearDrawingObjects(layer);
    }

    setSlide(
        layer: 1 | 2 | 3,
        slideJson: string,
        isAccessedViaHotspot: boolean,
        historyEntry?: PrimarySlideHistoryEntry
    ): void {
        this.yDoc.transact(() => {
            const isHistoryWrite = layer === 1 && historyEntry !== undefined;
            const previousPrimarySlide = isHistoryWrite ? this.readPrimarySlideIdentity() : null;
            const incomingCaption = isHistoryWrite ? this.readCaption(slideJson) : null;
            this.resetLayerForNewShowing(layer, {
                slide: { json: slideJson, isAccessedViaHotspot },
                resetViewport: true,
                revealState: 'fresh',
            });
            if (layer <= 1) {
                this.resetLayerForNewShowing(2, { slide: 'closed', resetViewport: true, revealState: 'fresh' });
            }
            if (layer <= 2) {
                this.resetLayerForNewShowing(3, { slide: 'closed', resetViewport: true, revealState: 'fresh' });
            }
            if (layer === 1 && historyEntry !== undefined
                && !this.isRedundantHistoryEntry(previousPrimarySlide, historyEntry, incomingCaption)) {
                this.primarySlideHistory.push([historyEntry]);
            }
        });
    }

    /**
     * True when recording `entry` would add nothing: the layer already showed the very
     * same thing, in the same lection.
     *
     * Collapsing is only safe when it cannot move time between lections. The server sums
     * the gap between consecutive entries, so a *missing* entry does not lose its time —
     * it hands it to the preceding one. Dropping a repeat inside one lection is therefore
     * free (both fall in the same bucket), while dropping a transition *between* lections
     * would credit the new lection's time to the old one.
     *
     * Identity is `(lectionId, slideId, caption)`, not `slideId` alone: client-synthesised
     * slides — the empty sentinel and every generated difficult-question slide — all carry
     * `id: 0` and differ only by caption. Keying on the id alone silently swallowed the
     * navigation from the sentinel to a question, and between two questions.
     *
     * Fails open: an unreadable previous slide records the entry rather than risking a
     * silent drop.
     */
    private isRedundantHistoryEntry(
        previous: { id: number; caption: string | null; lectionId: number | null } | null,
        entry: PrimarySlideHistoryEntry,
        incomingCaption: string | null,
    ): boolean {
        if (previous === null) return false;
        return previous.id === entry.slideId
            && previous.lectionId === entry.lectionId
            && previous.caption === incomingCaption;
    }

    /** Caption of a slide payload, or `null` if absent/unparseable — dedup treats it as "no caption". */
    private readCaption(slideJson: string): string | null {
        try {
            return (JSON.parse(slideJson) as { caption?: string }).caption ?? null;
        } catch {
            return null;
        }
    }

    private readPrimarySlideIdentity(): { id: number; caption: string | null; lectionId: number | null } | null {
        const json = this.lessonMap.get('primarySlide') as string | null | undefined;
        if (!json) return null;
        try {
            const parsed = JSON.parse(json) as { id?: number; caption?: string };
            if (typeof parsed.id !== 'number') {
                this.logger.error('primarySlide JSON is missing numeric `id` — dedup will fail open', undefined,
                    { jsonPrefix: json.slice(0, 120) });
                return null;
            }
            return {
                id: parsed.id,
                caption: parsed.caption ?? null,
                lectionId: this.lastRecordedLectionId(),
            };
        } catch (err) {
            this.logger.error('Corrupted primarySlide JSON in Y.Doc — dedup will fail open', err as Error,
                { jsonPrefix: json.slice(0, 120) });
            return null;
        }
    }

    /** Lection of the most recent history entry, i.e. the lection the layer is currently in. */
    private lastRecordedLectionId(): number | null {
        const length = this.primarySlideHistory.length;
        return length === 0 ? null : this.primarySlideHistory.get(length - 1).lectionId;
    }

    removeSlide(layer: 2 | 3): void {
        this.yDoc.transact(() => {
            // Closing a layer mints a new showing id so no surviving drawing can match it.
            // Viewport keys are left untouched (historical behavior — the layer is closed).
            if (layer === 2) {
                this.resetLayerForNewShowing(2, { slide: 'closed', resetViewport: false, revealState: 'fresh' });
            }
            this.resetLayerForNewShowing(3, { slide: 'closed', resetViewport: false, revealState: 'fresh' });
        });
    }

    /**
     * Panning does NOT mint a new showing id — deliberate, and the same holds for every
     * other within-showing setter here (`setSlideTextboardPage`, `setSlideScrollVideoFrame`,
     * the reveal and hotspot-deactivation setters). A showing ends when the slide is shown,
     * reset or closed; panning it is state *within* the showing, and minting here would
     * orphan every drawing already made on it. See {@link LessonYjsBinding.mintShowingId}.
     */
    setSlideViewBox(layer: 1 | 2 | 3, viewBox: string): void {
        const prefix = LessonYjsBinding.LAYER_PREFIX[layer];
        this.yDoc.transact(() => {
            this.lessonMap.set(`${prefix}SlideViewBox`, viewBox);
        });
    }

    /**
     * Persist the settled scroll-video frame index for a layer (`canvas-scroll-video-slides`).
     * Written debounced when scrubbing settles — never per wheel tick (the live stream is
     * ephemeral/awareness-only). Reset to `null` by the `setSlide` / `removeSlide` /
     * `resetSlideToInitial` cascades.
     */
    setSlideScrollVideoFrame(layer: 1 | 2 | 3, frame: number): void {
        const prefix = LessonYjsBinding.LAYER_PREFIX[layer];
        this.yDoc.transact(() => {
            this.lessonMap.set(`${prefix}SlideScrollVideoFrame`, frame);
        });
    }

    setSlideTextboardPage(layer: 1 | 2 | 3, pageNumber: number): void {
        const prefix = LessonYjsBinding.LAYER_PREFIX[layer];
        this.yDoc.transact(() => {
            this.lessonMap.set(`${prefix}SlideTextboardPage`, pageNumber);
        });
    }

    /**
     * Append a single dynamic content item to the layer's Y.Array.
     *
     * Each append encodes as an O(1) Yjs delta independent of array length —
     * unlike the previous Y.Map JSON-string read-modify-write, which made
     * every SignalR payload grow linearly with prior items (12+ kB per click
     * after a few appends; see task `dynamic-content-yarray-migration`).
     *
     * `itemJson` is the JSON-serialized `ITransferDynamicContentSlide`.
     */
    appendSlideDynamicContent(layer: 1 | 2 | 3, itemJson: string): void {
        this.dynamicContentArrays[layer].push([itemJson]);
    }

    resetSlideDynamicContent(layer: 1 | 2 | 3): void {
        this.clearDynamicContent(layer);
    }

    /**
     * Reset a layer back to its "slide just opened" state without changing the
     * slide identity: clears dynamicContent, restores the default viewBox,
     * resets textboardPage to 1, and clears the mutation Y.Array for the layer.
     *
     * Mirrors the `setSlide()` reset payload minus the slide-identity write —
     * used by the workspace same-slide re-click gesture.
     */
    resetSlideToInitial(layer: 1 | 2 | 3): void {
        this.yDoc.transact(() => {
            // A reset is a fresh SHOWING of the same slide: the slide identity stays, the
            // reveal epoch bumps (re-arms the beamer's hide animation), and a new showing
            // id is minted — any drawing stamped with the previous showing is now stale.
            this.resetLayerForNewShowing(layer, {
                slide: 'unchanged',
                resetViewport: true,
                revealState: 'bump-epoch',
            });
        });
    }

    setNavigationPreviewBasicSlides(json: string): void {
        this.lessonMap.set('navigationPreviewBasicSlides', json);
    }

    setNavigationPreviewNextSlides(json: string): void {
        this.lessonMap.set('navigationPreviewNextSlides', json);
    }

    /**
     * Reveal the perception image on a layer: pin it visible (`revealed: true`),
     * preserving the current show `epoch`. The reset back to hidden happens via the
     * `setSlide` / `resetSlideToInitial` cascades — there is no un-reveal here.
     */
    revealPerceptionSlide(layer: 1 | 2 | 3): void {
        const prefix = LessonYjsBinding.LAYER_PREFIX[layer];
        const current = this.readPerceptionRevealState(prefix);
        this.lessonMap.set(
            `${prefix}SlideRevealState`,
            JSON.stringify({ epoch: current.epoch, revealed: true } satisfies PerceptionRevealState),
        );
    }

    private readPerceptionRevealState(prefix: string): PerceptionRevealState {
        const raw = this.lessonMap.get(`${prefix}SlideRevealState`) as string | undefined;
        // Log corruption like the sibling `readPrimarySlideIdentity` does — a malformed synced
        // reveal-state silently restarting at epoch 0 could let a stale remote epoch match the
        // new one and skip the animation re-arm, so it must be observable, not swallowed.
        return parsePerceptionRevealState(raw, err =>
            this.logger.error('Corrupted SlideRevealState JSON in Y.Doc — falling back to hidden', err as Error, {
                prefix,
                jsonPrefix: raw?.slice(0, 120),
            }),
        );
    }

    /**
     * Deactivate a hotspot on a layer (D6/C14): add its index to the layer's synced
     * deactivated-set (idempotent — {@link parseDeactivatedHotspots} de-dupes). The re-arm back to
     * the empty set happens via the `setSlide` / `removeSlide` / `resetSlideToInitial` cascades —
     * there is no re-activate here.
     */
    deactivateHotspot(layer: 1 | 2 | 3, index: number): void {
        if (!Number.isInteger(index) || index < 0) return;
        const prefix = LessonYjsBinding.LAYER_PREFIX[layer];
        const current = this.readDeactivatedHotspots(prefix);
        if (current.includes(index)) return; // already deactivated — avoid a no-op Y.Doc transaction
        this.lessonMap.set(`${prefix}SlideDeactivatedHotspots`, JSON.stringify([...current, index]));
    }

    private readDeactivatedHotspots(prefix: string): number[] {
        const raw = this.lessonMap.get(`${prefix}SlideDeactivatedHotspots`) as string | undefined;
        return parseDeactivatedHotspots(raw, err =>
            this.logger.error('Corrupted SlideDeactivatedHotspots JSON in Y.Doc — falling back to empty', err as Error, {
                prefix,
                jsonPrefix: raw?.slice(0, 120),
            }),
        );
    }

    /**
     * Initialize Y.Map with default values (fresh lesson).
     * Only sets values that are not yet present in the map.
     */
    initializeDefaults(): void {
        this.yDoc.transact(() => {
            for (const key of SYNCED_LESSON_STATE_KEYS) {
                if (!this.lessonMap.has(key)) {
                    this.lessonMap.set(
                        key,
                        SYNCED_LESSON_STATE_DEFAULTS[key as keyof SyncedLessonState]
                    );
                }
            }
        });
    }

    /**
     * Read current Y.Map state as a SyncedLessonState snapshot.
     */
    getSnapshot(): SyncedLessonState {
        const get = <T>(key: string, fallback: T): T =>
            (this.lessonMap.get(key) as T) ?? fallback;
        const d = SYNCED_LESSON_STATE_DEFAULTS;
        return {
            lessonStatus: get('lessonStatus', d.lessonStatus),
            paused: get('paused', d.paused),
            pauseImageUrl: get('pauseImageUrl', null),
            isShowingStudentRegistrationCode: get('isShowingStudentRegistrationCode', d.isShowingStudentRegistrationCode),
            isLessonStopPending: get('isLessonStopPending', d.isLessonStopPending),
            activeExtensionApp: get('activeExtensionApp', null),

            primarySlide: get('primarySlide', null),
            primarySlideIsAccessedViaHotspot: get('primarySlideIsAccessedViaHotspot', d.primarySlideIsAccessedViaHotspot),
            primarySlideViewBox: get('primarySlideViewBox', d.primarySlideViewBox),
            primarySlideTextboardPage: get('primarySlideTextboardPage', d.primarySlideTextboardPage),
            primarySlideRevealState: get('primarySlideRevealState', d.primarySlideRevealState),
            primarySlideDeactivatedHotspots: get('primarySlideDeactivatedHotspots', d.primarySlideDeactivatedHotspots),
            primarySlideScrollVideoFrame: get('primarySlideScrollVideoFrame', d.primarySlideScrollVideoFrame),
            primarySlideShowingId: get('primarySlideShowingId', d.primarySlideShowingId),

            secondarySlide: get('secondarySlide', null),
            secondarySlideIsAccessedViaHotspot: get('secondarySlideIsAccessedViaHotspot', d.secondarySlideIsAccessedViaHotspot),
            secondarySlideViewBox: get('secondarySlideViewBox', d.secondarySlideViewBox),
            secondarySlideTextboardPage: get('secondarySlideTextboardPage', d.secondarySlideTextboardPage),
            secondarySlideRevealState: get('secondarySlideRevealState', d.secondarySlideRevealState),
            secondarySlideDeactivatedHotspots: get('secondarySlideDeactivatedHotspots', d.secondarySlideDeactivatedHotspots),
            secondarySlideScrollVideoFrame: get('secondarySlideScrollVideoFrame', d.secondarySlideScrollVideoFrame),
            secondarySlideShowingId: get('secondarySlideShowingId', d.secondarySlideShowingId),

            tertiarySlide: get('tertiarySlide', null),
            tertiarySlideIsAccessedViaHotspot: get('tertiarySlideIsAccessedViaHotspot', d.tertiarySlideIsAccessedViaHotspot),
            tertiarySlideViewBox: get('tertiarySlideViewBox', d.tertiarySlideViewBox),
            tertiarySlideTextboardPage: get('tertiarySlideTextboardPage', d.tertiarySlideTextboardPage),
            tertiarySlideRevealState: get('tertiarySlideRevealState', d.tertiarySlideRevealState),
            tertiarySlideDeactivatedHotspots: get('tertiarySlideDeactivatedHotspots', d.tertiarySlideDeactivatedHotspots),
            tertiarySlideScrollVideoFrame: get('tertiarySlideScrollVideoFrame', d.tertiarySlideScrollVideoFrame),
            tertiarySlideShowingId: get('tertiarySlideShowingId', d.tertiarySlideShowingId),

            navigationPreviewBasicSlides: get('navigationPreviewBasicSlides', d.navigationPreviewBasicSlides),
            navigationPreviewNextSlides: get('navigationPreviewNextSlides', d.navigationPreviewNextSlides),
        };
    }

    // --- Display-state handoff (lesson start/stop document swap) ---

    /**
     * Export the display half of this document for a handoff (draft → lesson on
     * start, lesson → draft on stop).
     *
     * Schema-driven: the Y.Map half is every `SYNCED_LESSON_STATE_KEYS` member NOT
     * in {@link LESSON_SCOPED_KEYS} — a display field added to the schema is carried
     * with no change here. Deliberately NOT routed through `getSnapshot()`: its
     * enumerated field list is exactly the per-field maintenance this pair replaces.
     * The array half copies the per-layer dynamic-content, HTML-element-mutation and
     * drawing-object arrays plus the drawing metadata maps.
     *
     * What does NOT carry: `LESSON_SCOPED_KEYS` members (lesson lifecycle/overlay
     * flags) and the primary-slide history (lesson-scoped reporting — it dies with
     * this document; the receiving document re-seeds it via
     * `seedPrimarySlideHistoryFromCurrentSlide()` on lesson start).
     *
     * The counterpart `importDisplayState()` must run on the receiving document
     * BEFORE hydration — see `LessonYjsService.initialize()` / `initializeOffline()`.
     */
    exportDisplayState(): DisplayStateSnapshot {
        const lessonMap: Record<string, unknown> = {};
        for (const key of SYNCED_LESSON_STATE_KEYS) {
            if (LESSON_SCOPED_KEYS.has(key)) continue;
            const value = this.lessonMap.get(key);
            if (value === undefined) continue; // absent key — leave the importing doc's default in place
            lessonMap[key] = value;
        }
        const perLayer = <T>(read: (layer: 1 | 2 | 3) => T): Record<1 | 2 | 3, T> =>
            ({ 1: read(1), 2: read(2), 3: read(3) });
        return {
            lessonMap,
            dynamicContent: perLayer(layer => this.dynamicContentArrays[layer].toArray()),
            htmlElementMutations: perLayer(layer => this.mutationArrays[layer].toArray()),
            drawingObjects: perLayer(layer => this.drawingObjectArrays[layer].toArray().map(m => m.toJSON() as Record<string, unknown>)),
            drawingMetadata: perLayer(layer => this.getDrawingMetadataMap(layer).toJSON() as Record<string, unknown>),
        };
    }

    /**
     * Import a display-state snapshot produced by {@link exportDisplayState} into
     * this document, as fresh `LOCAL_ONLY` writes in one transaction.
     *
     * Plain JSON re-writes — never `Y.encodeStateAsUpdate`/`Y.applyUpdate`, which
     * would import the source document's client ids and clock entries and pollute
     * this document's clean clock (the property that justifies the swap).
     *
     * What carries: the display subset of the lesson Y.Map, dynamic content,
     * HTML-element mutations, drawing objects (reconstructed as fresh Y.Maps) and
     * drawing metadata. What does not: `LESSON_SCOPED_KEYS` members — skipped with
     * a logged error if present, as insurance against a hand-built snapshot — and
     * the primary-slide history (never part of the snapshot). Malformed
     * (non-object) drawing entries are skipped and logged, mirroring the slide-JSON
     * parse guards elsewhere in this class. Empty/absent sections are a clean no-op.
     *
     * MUST run before hydration (`hydratedSubject.next(true)`) — two independent
     * reasons, see `LessonYjsService`: (1) the fabric rebind's `syncFromYjs()` then
     * reconciles against already-populated arrays, clearing stale untracked strokes
     * and re-adding carried ones as tracked instead of leaving duplicates; (2)
     * `changes$`'s `startWith(getSnapshot())` on the hydration edge carries the
     * imported state to the display Signals in one emission.
     */
    importDisplayState(snapshot: DisplayStateSnapshot): void {
        this.yDoc.transact(() => {
            for (const [key, value] of Object.entries(snapshot.lessonMap ?? {})) {
                if (LESSON_SCOPED_KEYS.has(key)) {
                    this.logger.error(
                        'importDisplayState: snapshot contains a lesson-scoped key — skipped (must never cross a handoff)',
                        undefined, { key });
                    continue;
                }
                if (!SYNCED_LESSON_STATE_KEYS.has(key)) continue; // not part of the schema
                this.lessonMap.set(key, value);
            }
            for (const layer of [1, 2, 3] as const) {
                const dynamicContent = snapshot.dynamicContent?.[layer] ?? [];
                if (dynamicContent.length > 0) {
                    this.dynamicContentArrays[layer].push([...dynamicContent]);
                }
                const mutations = snapshot.htmlElementMutations?.[layer] ?? [];
                if (mutations.length > 0) {
                    this.mutationArrays[layer].push([...mutations]);
                }
                const drawingObjects: Y.Map<unknown>[] = [];
                for (const entry of snapshot.drawingObjects?.[layer] ?? []) {
                    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
                        this.logger.error('importDisplayState: malformed drawing object entry — skipped',
                            undefined, { layer, entryType: typeof entry });
                        continue;
                    }
                    drawingObjects.push(new Y.Map(Object.entries(entry)));
                }
                if (drawingObjects.length > 0) {
                    this.drawingObjectArrays[layer].push(drawingObjects);
                }
                const metadataMap = this.getDrawingMetadataMap(layer);
                for (const [key, value] of Object.entries(snapshot.drawingMetadata?.[layer] ?? {})) {
                    metadataMap.set(key, value);
                }
            }
        }, LessonYjsBinding.LOCAL_ONLY);
    }

    // --- Mutation array methods (Phase 4: DomMutation sync via Yjs) ---

    /** Append a JSON-serialized DomMutation[] batch to a layer's Y.Array. */
    appendMutationBatch(layer: 1 | 2 | 3, batchJson: string): void {
        this.mutationArrays[layer].push([batchJson]);
    }

    /** Clear all mutation batches for a layer. Called internally by setSlide()/removeSlide(). */
    clearMutations(layer: 1 | 2 | 3): void {
        const arr = this.mutationArrays[layer];
        if (arr.length > 0) {
            arr.delete(0, arr.length);
        }
    }

    /** Get all current mutation batches for a layer (for late-join replay). */
    getMutationBatches(layer: 1 | 2 | 3): string[] {
        return this.mutationArrays[layer].toArray();
    }

    // --- Dynamic content array methods ---

    /**
     * Clear all dynamic content items for a layer. Called by `setSlide()`,
     * `removeSlide()`, `resetSlideDynamicContent()`, and `resetSlideToInitial()`.
     * The slide-identity suppression in `attachDynamicContentObservers` determines
     * whether the resulting `dynamicContentCleared$` event fires (it fires for
     * the reset paths and is suppressed for the slide-change cascade).
     */
    clearDynamicContent(layer: 1 | 2 | 3): void {
        const arr = this.dynamicContentArrays[layer];
        if (arr.length > 0) {
            arr.delete(0, arr.length);
        }
    }

    // --- Drawing-object array methods (fabric.js canvas migration) ---

    /**
     * Raw per-layer `Y.Array<Y.Map>` of drawing objects, for the data-access-internal
     * drawing-canvas service to construct a `FabricYjsBinding` over. Intentionally
     * NOT surfaced through `ClassroomFacade` — raw Yjs handles stay inside the lib.
     */
    getDrawingObjectsArray(layer: 1 | 2 | 3): Y.Array<Y.Map<unknown>> {
        return this.drawingObjectArrays[layer];
    }

    /**
     * Identity of the layer's current showing (`slide-showing-identity`), or `''` before
     * any slide has been shown on it. Read by `LessonDrawingService` to stamp locally
     * created drawings and to reject ones carrying a different showing.
     */
    getShowingId(layer: 1 | 2 | 3): string {
        const prefix = LessonYjsBinding.LAYER_PREFIX[layer];
        return (this.lessonMap.get(`${prefix}SlideShowingId`) as string | undefined) ?? '';
    }

    /**
     * Per-layer (doc-attached, normally empty) metadata Y.Map used to satisfy the
     * `FabricYjsBinding` constructor. Classroom sets the background locally
     * (`setBackgroundLocal`) and never writes synced background into this map.
     */
    getDrawingMetadataMap(layer: 1 | 2 | 3): Y.Map<unknown> {
        return this.yDoc.getMap(DRAWING_METADATA_MAP_KEYS[layer]);
    }

    /**
     * Clear all drawing objects for a layer. Called by the `setSlide()`/`removeSlide()`
     * cascade and `resetSlideToInitial()`. The clear runs in a non-`FABRIC_ORIGIN`
     * transaction, so the layer's `FabricYjsBinding` observer reconciles the canvas
     * (removes the objects) automatically — no imperative canvas teardown needed.
     */
    clearDrawingObjects(layer: 1 | 2 | 3): void {
        const arr = this.drawingObjectArrays[layer];
        if (arr.length > 0) {
            arr.delete(0, arr.length);
        }
    }

    /** Get all current dynamic content items (JSON strings) for a layer (for late-join replay). */
    getDynamicContentSnapshot(layer: 1 | 2 | 3): string[] {
        return this.dynamicContentArrays[layer].toArray();
    }

    // --- Primary slide history ---

    /**
     * Snapshot of the append-only primary-slide history Y.Array.
     * Used by the bridge effect to feed `startWith()` after late-join hydration —
     * the observer fires only on subsequent changes, so the initial snapshot must
     * be read imperatively. NgRx consumers should select from the mirrored slice
     * (`selectPrimarySlideHistory`) instead of calling this directly.
     */
    getPrimarySlideHistorySnapshot(): PrimarySlideHistoryEntry[] {
        return this.primarySlideHistory.toArray();
    }

    /**
     * Clean up observer. Call when the binding is no longer needed.
     */
    destroy(): void {
        if (this.observer) {
            this.lessonMap.unobserve(this.observer);
            this.observer = undefined;
        }
        for (const unobserve of this.mutationObservers) {
            unobserve();
        }
        this.mutationObservers = [];
        for (const unobserve of this.dynamicContentObservers) {
            unobserve();
        }
        this.dynamicContentObservers = [];
        for (const unobserve of this.historyObservers) {
            unobserve();
        }
        this.historyObservers = [];
        this.changesSubject.complete();
        this.mutationBatchSubject.complete();
        this.mutationBatchesClearedSubject.complete();
        this.dynamicContentAddedSubject.complete();
        this.dynamicContentClearedSubject.complete();
        this.historySubject.complete();
    }

    private attachObserver(): void {
        this.observer = (event: Y.YMapEvent<unknown>, transaction: Y.Transaction) => {
            if (transaction.origin === LessonYjsBinding.LOCAL_ONLY) return;
            const changes: Partial<SyncedLessonState> = {};
            let hasChanges = false;

            for (const key of event.keysChanged) {
                if (!SYNCED_LESSON_STATE_KEYS.has(key)) {
                    continue;
                }
                const value = this.lessonMap.get(key);
                (changes as Record<string, unknown>)[key] = value ?? null;
                hasChanges = true;
            }

            if (!hasChanges) return;

            // Yjs swallows synchronous exceptions from observer callbacks (it logs to
            // console.error and continues). Without this guard, a downstream subscriber
            // throw would silently drop the NgRx dispatch — bypassing the bridge effect's
            // catchError. Route via the configured logger so failures are observable.
            try {
                this.changesSubject.next(changes);
            } catch (err) {
                this.logger.error('LessonYjsBinding.changes$ subscriber threw', err as Error);
            }
        };

        this.lessonMap.observe(this.observer);
    }

    private static readonly SLIDE_KEYS = ['primarySlide', 'secondarySlide', 'tertiarySlide'] as const;

    private attachMutationObservers(): void {
        for (const layer of [1, 2, 3] as const) {
            const arr = this.mutationArrays[layer];
            const observer = (event: Y.YArrayEvent<string>, transaction: Y.Transaction) => {
                let sawDelete = false;
                for (const delta of event.delta) {
                    if (delta.insert) {
                        // Skip local INSERTS — the writer already applied them to its own DOM,
                        // emitting would double-apply. (Deletes are NOT skipped: see cleared-event
                        // handling below — the writer's own clearMutations needs the cleared signal
                        // so the slide-layer reaction is symmetric with the receiver.)
                        if (transaction.local) continue;
                        for (const batch of delta.insert as string[]) {
                            try {
                                this.mutationBatchSubject.next({ layer, batch });
                            } catch (err) {
                                this.logger.error('LessonYjsBinding.mutationBatchAdded$ subscriber threw', err as Error, { layer });
                            }
                        }
                    } else if (delta.delete !== undefined && delta.delete > 0) {
                        sawDelete = true;
                    }
                }
                // Surface a "cleared" signal when a delete brings the array to empty
                // AND this is a user-reset gesture — NOT a slide-change cascade.
                // `setSlide()` and `removeSlide()` write a `<layer>Slide` field as
                // part of the same transaction; we suppress in that case so downstream
                // consumers (e.g. `videoPlayer.restart()` in `createResetDynamicContent$`)
                // don't fire on the freshly-rendered new slide. `resetSlideToInitial()`
                // doesn't touch slide identity fields, so the cleared event still fires
                // for the same-slide reset.
                //
                // The suppression decision is captured synchronously (transaction.changed
                // is only valid inside the observer call). The actual subject emission is
                // deferred via `emitAfterTransaction` so it runs AFTER Y.applyUpdate
                // completes — keeps re-entrancy out of the Y.Doc transaction context.
                const shouldEmitCleared = sawDelete && arr.length === 0 && !this.transactionChangedSlideIdentity(transaction);
                if (shouldEmitCleared) {
                    emitAfterTransaction(
                        () => this.mutationBatchesClearedSubject.next({ layer }),
                        (err) => this.logger.error('LessonYjsBinding.mutationBatchesCleared$ subscriber threw', err as Error, { layer }),
                    );
                }
            };
            arr.observe(observer);
            this.mutationObservers.push(() => arr.unobserve(observer));
        }
    }

    private attachDynamicContentObservers(): void {
        for (const layer of [1, 2, 3] as const) {
            const arr = this.dynamicContentArrays[layer];
            const observer = (event: Y.YArrayEvent<string>, transaction: Y.Transaction) => {
                let sawDelete = false;
                for (const delta of event.delta) {
                    if (delta.insert) {
                        // SYMMETRIC: do NOT skip local inserts. Unlike `mutationBatchAdded$`,
                        // the writer of a dynamic-content item has no pre-write local DOM
                        // path — its UI updates from this very event. Suppressing locals
                        // would leave the writer's own UI stale.
                        for (const itemJson of delta.insert as string[]) {
                            try {
                                this.dynamicContentAddedSubject.next({ layer, itemJson });
                            } catch (err) {
                                this.logger.error('LessonYjsBinding.dynamicContentAdded$ subscriber threw', err as Error, { layer });
                            }
                        }
                    } else if (delta.delete !== undefined && delta.delete > 0) {
                        sawDelete = true;
                    }
                }
                // Cleared semantics mirror `mutationBatchesCleared$`: fire when a
                // delete empties the array AND the transaction is NOT a slide-identity
                // cascade (setSlide / removeSlide), deferred via `emitAfterTransaction`
                // so the emission lands after Y.applyUpdate or the local transact callback.
                const shouldEmitCleared = sawDelete && arr.length === 0 && !this.transactionChangedSlideIdentity(transaction);
                if (shouldEmitCleared) {
                    emitAfterTransaction(
                        () => this.dynamicContentClearedSubject.next({ layer }),
                        (err) => this.logger.error('LessonYjsBinding.dynamicContentCleared$ subscriber threw', err as Error, { layer }),
                    );
                }
            };
            arr.observe(observer);
            this.dynamicContentObservers.push(() => arr.unobserve(observer));
        }
    }

    private transactionChangedSlideIdentity(transaction: Y.Transaction): boolean {
        // Y.Transaction.changed is keyed by AbstractType<YEvent<any>>; YMap<unknown> is a
        // subtype but TS variance rejects the get(). Cast through unknown to match the API.
        const changedKeys = transaction.changed.get(this.lessonMap as unknown as Y.AbstractType<Y.YEvent<Y.AbstractType<unknown>>>);
        if (!changedKeys) return false;
        for (const key of LessonYjsBinding.SLIDE_KEYS) {
            if (changedKeys.has(key)) return true;
        }
        return false;
    }

    private attachHistoryObserver(): void {
        // Fires for both local and remote writes. We don't filter by `transaction.local`
        // here (unlike `attachMutationObservers`) because the consumer is an NgRx mirror
        // that needs to stay in lock-step on every client, including the writer —
        // selectors must reflect the local append immediately, not only after a server
        // round-trip.
        const observer = () => {
            // try/catch for the same reason as attachObserver: Yjs swallows observer
            // exceptions, and the downstream bridge effect's catchError can't see a
            // throw that escapes a Subject's synchronous notification path.
            try {
                this.historySubject.next(this.primarySlideHistory.toArray());
            } catch (err) {
                this.logger.error('LessonYjsBinding.primarySlideHistory$ snapshot/emit failed', err as Error);
            }
        };
        this.primarySlideHistory.observe(observer);
        this.historyObservers.push(() => this.primarySlideHistory.unobserve(observer));
    }
}
